import {
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  type AnyPgColumn,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { orderItems } from "./orders";
import { bundleAssemblies, productVariants, stockMovements } from "./products";

export type ImportOrderStatus =
  | "draft"
  | "ordered"
  | "at_carrier"
  | "partially_received"
  | "received"
  | "cancelled";

export type ImportOrderSource =
  | "taobao"
  | "1688"
  | "pinduoduo"
  | "other";

/** @deprecated legacy column `source_app` — prefer `source` (Phase 13) */
export type ImportSourceApp =
  | "taobao"
  | "1688"
  | "shopee_cn"
  | "manual"
  | "other";

export type ImportOrderTrackingStatus =
  | "in_transit"
  | "arrived"
  | "delivered"
  | "unknown";

export type ImportOrderReceiptStatus = "confirmed" | "void";

/**
 * ใบสั่งนำเข้า — Phase 13 upgrade (landed cost + partial receive)
 */
export const importOrders = pgTable(
  "import_orders",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** เลขอ้างอิงภายในระบบ */
    orderNumber: text("order_number").notNull().unique(),
    /** เลข order จากแอปจีน (Taobao / 1688 / Pinduoduo) */
    externalOrderNo: text("external_order_no").notNull(),
    status: text("status").notNull().default("draft"),
    source: text("source").notNull().default("other"),
    /** @deprecated legacy — mirror ของ `source` ระหว่าง migrate */
    sourceApp: text("source_app").notNull().default("manual"),
    /** @deprecated legacy — ใช้ `externalOrderNo` แทน */
    externalRef: text("external_ref"),
    currency: text("currency").notNull().default("CNY"),
    exchangeRate: numeric("exchange_rate", { precision: 12, scale: 4 })
      .notNull()
      .default("1"),
    goodsSubtotalForeign: numeric("goods_subtotal_foreign", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    goodsSubtotalLak: numeric("goods_subtotal_lak", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    shippingTotalLak: numeric("shipping_total_lak", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    landedTotalLak: numeric("landed_total_lak", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    orderedAt: timestamp("ordered_at", {
      withTimezone: true,
      mode: "date",
    }),
    expectedArrivalDate: timestamp("expected_arrival_date", {
      withTimezone: true,
      mode: "date",
    }),
    /** @deprecated legacy — ใช้ `expectedArrivalDate` */
    carrier: text("carrier"),
    carrierNote: text("carrier_note"),
    /** @deprecated legacy — ย้ายไป `import_order_trackings` */
    trackingInbound: text("tracking_inbound"),
    /** @deprecated legacy — ใช้ `expectedArrivalDate` */
    expectedAt: timestamp("expected_at", {
      withTimezone: true,
      mode: "date",
    }),
    note: text("note"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("import_orders_by_status").on(t.status),
    index("import_orders_by_created").on(t.createdAt),
    index("import_orders_by_external_order_no").on(t.externalOrderNo),
    index("import_orders_by_expected_arrival").on(t.expectedArrivalDate),
  ],
);

export const IMPORT_ORDER_LINE_MATCH_STATUSES = [
  "pending",
  "matched",
  "skipped",
] as const;

export type ImportOrderLineMatchStatus =
  (typeof IMPORT_ORDER_LINE_MATCH_STATUSES)[number];

export const importOrderLines = pgTable(
  "import_order_lines",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    importOrderId: text("import_order_id")
      .notNull()
      .references(() => importOrders.id, { onDelete: "cascade" }),
    variantId: text("variant_id").references(() => productVariants.id, {
      onDelete: "restrict",
    }),
    matchStatus: text("match_status").notNull().default("matched"),
    externalTitle: text("external_title"),
    externalUrl: text("external_url"),
    externalImageKey: text("external_image_key"),
    matchedAt: timestamp("matched_at", {
      withTimezone: true,
      mode: "date",
    }),
    matchedBy: text("matched_by"),
    skippedAt: timestamp("skipped_at", {
      withTimezone: true,
      mode: "date",
    }),
    skipNote: text("skip_note"),
    description: text("description"),
    quantityOrdered: integer("quantity_ordered").notNull(),
    quantityReceived: integer("quantity_received").notNull().default(0),
    shortQuantity: integer("short_quantity").notNull().default(0),
    unitPriceForeign: numeric("unit_price_foreign", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    goodsCostForeign: numeric("goods_cost_foreign", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    goodsCostLak: numeric("goods_cost_lak", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    allocatedShippingLak: numeric("allocated_shipping_lak", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    landedCostLak: numeric("landed_cost_lak", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    unitLandedCostLak: numeric("unit_landed_cost_lak", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    /** snapshot ชื่อสินค้า ณ เวลาสร้างใบ */
    productName: text("product_name").notNull(),
    variantName: text("variant_name").notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("import_order_lines_by_order").on(t.importOrderId),
    index("import_order_lines_by_variant").on(t.variantId),
    index("import_order_lines_by_match_status").on(t.matchStatus),
  ],
);

export const importOrderTrackings = pgTable(
  "import_order_trackings",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    importOrderId: text("import_order_id")
      .notNull()
      .references(() => importOrders.id, { onDelete: "cascade" }),
    trackingNo: text("tracking_no").notNull(),
    carrierName: text("carrier_name"),
    status: text("status").notNull().default("in_transit"),
    shippedAt: timestamp("shipped_at", {
      withTimezone: true,
      mode: "date",
    }),
    arrivedAt: timestamp("arrived_at", {
      withTimezone: true,
      mode: "date",
    }),
    note: text("note"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("import_order_trackings_by_order").on(t.importOrderId),
    index("import_order_trackings_by_tracking_no").on(t.trackingNo),
  ],
);

export const importOrderReceipts = pgTable(
  "import_order_receipts",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    importOrderId: text("import_order_id")
      .notNull()
      .references(() => importOrders.id, { onDelete: "restrict" }),
    status: text("status").notNull().default("confirmed"),
    receivedAt: timestamp("received_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    note: text("note"),
    createdBy: text("created_by"),
    voidedAt: timestamp("voided_at", {
      withTimezone: true,
      mode: "date",
    }),
    voidedBy: text("voided_by"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("import_order_receipts_by_order").on(t.importOrderId),
    index("import_order_receipts_by_status").on(t.status),
  ],
);

/**
 * ໃບຮັບເຂົ້າສິນຄ້າ (Goods Receipt) — 1 ໃບ = 1 lot ຮັບເຂົ້າ
 * ມັກກົງກັບ 1 ກ່ອງ / 1 tracking ຂອງຂົນສົ່ງ (ເຊັ່ນ ຮຸ່ງອາລຸນ)
 */
export const goodsReceipts = pgTable(
  "goods_receipts",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    receiptNumber: text("receipt_number").notNull().unique(),
    status: text("status").notNull().default("draft"),
    receivedAt: timestamp("received_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    carrier: text("carrier"),
    carrierNote: text("carrier_note"),
    trackingNumber: text("tracking_number"),
    shippingFeeTotal: numeric("shipping_fee_total", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    otherFeesTotal: numeric("other_fees_total", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    shippingAllocationMethod: text("shipping_allocation_method")
      .notNull()
      .default("by_value"),
    purchaseSubtotal: numeric("purchase_subtotal", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    landedSubtotal: numeric("landed_subtotal", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    note: text("note"),
    importOrderId: text("import_order_id").references(() => importOrders.id, {
      onDelete: "set null",
    }),
    createdBy: text("created_by"),
    confirmedAt: timestamp("confirmed_at", {
      withTimezone: true,
      mode: "date",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("goods_receipts_by_status").on(t.status),
    index("goods_receipts_by_received_at").on(t.receivedAt),
  ],
);

export const goodsReceiptLines = pgTable(
  "goods_receipt_lines",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    receiptId: text("receipt_id")
      .notNull()
      .references(() => goodsReceipts.id, { onDelete: "cascade" }),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    /** จำนวนหน่วยรับเข้า (ตาม receive_unit ตอนบันทึก) */
    quantity: integer("quantity").notNull(),
    /** จำนวนหน่วยขายที่จะบวกเข้าสต็อก = quantity × conversion_factor */
    stockQuantity: integer("stock_quantity").notNull(),
    conversionFactor: numeric("conversion_factor", {
      precision: 12,
      scale: 4,
    })
      .notNull()
      .default("1"),
    stockUnitLabel: text("stock_unit_label").notNull().default("ຊິ້ນ"),
    receiveUnitLabel: text("receive_unit_label"),
    unitPurchaseCost: numeric("unit_purchase_cost", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    shippingAllocated: numeric("shipping_allocated", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    unitLandedCost: numeric("unit_landed_cost", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    lineLandedTotal: numeric("line_landed_total", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    productName: text("product_name").notNull(),
    variantName: text("variant_name").notNull(),
  },
  (t) => [index("goods_receipt_lines_by_receipt").on(t.receiptId)],
);

/** ย้าย/แปลงสต็อกจาก variant เดิมไป variant ใหม่ (Phase 12) */
export const stockTransfers = pgTable(
  "stock_transfers",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    sourceVariantId: text("source_variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    targetVariantId: text("target_variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    sourceQuantity: integer("source_quantity").notNull(),
    targetQuantity: integer("target_quantity").notNull(),
    targetPerSource: numeric("target_per_source", {
      precision: 12,
      scale: 4,
    }).notNull(),
    totalLandedCost: numeric("total_landed_cost", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    unitLandedCost: numeric("unit_landed_cost", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    note: text("note"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("stock_transfers_by_source").on(t.sourceVariantId),
    index("stock_transfers_by_target").on(t.targetVariantId),
    index("stock_transfers_by_created").on(t.createdAt),
  ],
);

/**
 * Lot ສະຕັອກ — ສ້າງຕອນ confirm goods receipt
 * ໃຊ້ສຳລັບ FIFO COGS (Phase 7.4) — ດຶງ lot ເກົ່າກ່ອນຕອນຂາຍ
 */
export const stockLots = pgTable(
  "stock_lots",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    receiptId: text("receipt_id").references(() => goodsReceipts.id, {
      onDelete: "restrict",
    }),
    receiptLineId: text("receipt_line_id").references(
      () => goodsReceiptLines.id,
      { onDelete: "restrict" },
    ),
    /** lot จากการประกอบชุด (Phase 11) — ไม่มี goods receipt */
    bundleAssemblyId: text("bundle_assembly_id").references(
      () => bundleAssemblies.id,
      { onDelete: "restrict" },
    ),
    /** lot จากการย้าย/แปลงหน่วยสินค้า (Phase 12) */
    stockTransferId: text("stock_transfer_id").references(
      () => stockTransfers.id,
      { onDelete: "restrict" },
    ),
    /** lot จากการรับเข้า import order (Phase 13) */
    importOrderReceiptLineId: text("import_order_receipt_line_id").references(
      (): AnyPgColumn => importOrderReceiptLines.id,
      { onDelete: "restrict" },
    ),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    quantityReceived: integer("quantity_received").notNull(),
    quantityRemaining: integer("quantity_remaining").notNull(),
    unitPurchaseCost: numeric("unit_purchase_cost", {
      precision: 12,
      scale: 2,
    }).notNull(),
    unitLandedCost: numeric("unit_landed_cost", {
      precision: 12,
      scale: 2,
    }).notNull(),
    receivedAt: timestamp("received_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("stock_lots_by_variant_received").on(t.variantId, t.receivedAt),
    index("stock_lots_by_receipt").on(t.receiptId),
    index("stock_lots_by_assembly").on(t.bundleAssemblyId),
    index("stock_lots_by_transfer").on(t.stockTransferId),
    index("stock_lots_by_import_receipt_line").on(t.importOrderReceiptLineId),
  ],
);

export const importOrderReceiptLines = pgTable(
  "import_order_receipt_lines",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    receiptId: text("receipt_id")
      .notNull()
      .references(() => importOrderReceipts.id, { onDelete: "cascade" }),
    importOrderLineId: text("import_order_line_id")
      .notNull()
      .references(() => importOrderLines.id, { onDelete: "restrict" }),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    receivedQuantity: integer("received_quantity").notNull(),
    unitLandedCostLak: numeric("unit_landed_cost_lak", {
      precision: 12,
      scale: 2,
    }).notNull(),
    landedCostLak: numeric("landed_cost_lak", {
      precision: 12,
      scale: 2,
    }).notNull(),
    stockLotId: text("stock_lot_id").references((): AnyPgColumn => stockLots.id, {
      onDelete: "set null",
    }),
    stockMovementId: text("stock_movement_id").references(
      () => stockMovements.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("import_order_receipt_lines_by_receipt").on(t.receiptId),
    index("import_order_receipt_lines_by_order_line").on(t.importOrderLineId),
  ],
);

/**
 * COGS ต่อ order line — บันทึก FIFO ตอนขาย (Phase 7.4)
 */
export const orderItemCogs = pgTable(
  "order_item_cogs",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderItemId: text("order_item_id")
      .notNull()
      .references(() => orderItems.id, { onDelete: "cascade" }),
    stockLotId: text("stock_lot_id")
      .notNull()
      .references(() => stockLots.id, { onDelete: "restrict" }),
    quantity: integer("quantity").notNull(),
    unitLandedCost: numeric("unit_landed_cost", {
      precision: 12,
      scale: 2,
    }).notNull(),
    lineCogs: numeric("line_cogs", { precision: 12, scale: 2 }).notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("order_item_cogs_by_order_item").on(t.orderItemId),
    index("order_item_cogs_by_created").on(t.createdAt),
  ],
);

/**
 * Lot ທີ່ຜູ້ໃຊ້ເລືອກຕອນສ້າງ/ແກ້ draft — ໃຊ້ตอน paid แทน FIFO อัตโนมัติ
 */
export const orderItemLotPicks = pgTable(
  "order_item_lot_picks",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderItemId: text("order_item_id")
      .notNull()
      .references(() => orderItems.id, { onDelete: "cascade" }),
    stockLotId: text("stock_lot_id")
      .notNull()
      .references(() => stockLots.id, { onDelete: "restrict" }),
    quantity: integer("quantity").notNull(),
  },
  (t) => [index("order_item_lot_picks_by_order_item").on(t.orderItemId)],
);

export type GoodsReceiptStatus = "draft" | "confirmed" | "void";

export type ShippingAllocationMethod = "by_value" | "by_quantity" | "manual";

export type InventoryCarrier = "hung_arun" | "anusith" | "other";
